<template>
  <div class="flex flex-col">
    <div class="flex items-center justify-between">
      <h2>Novedades</h2>
      <router-link
        v-if="hasPermission('edit_posts')"
        :to="{ name: 'postsCreateForm' }"
        class="primary-button mx-0"
        >Nueva novedad</router-link
      >
    </div>

    <div class="flex flex-col sm:flex-row sm:space-x-4 sm:items-end mt-4">
      <custom-search
        class="w-full sm:w-[400px]"
        placeholder="Buscar por título"
        v-model="searchText"
        @confirm="search"
        @clear="search"
      />

      <div class="flex flex-col mt-2 sm:mt-0">
        <label for="status_filter" class="text-sm">Estado</label>
        <select
          id="status_filter"
          v-model="statusFilter"
          class="rounded border border-content-100 px-2 py-1"
          @change="search"
        >
          <option value="">Todas</option>
          <option value="true">Publicadas</option>
          <option value="false">Borradores</option>
        </select>
      </div>
    </div>

    <div v-if="!loading" class="card mt-6">
      <table class="table-auto text-sm mt-2 bg-white w-full">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Título</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Publicación</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Productos</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="post in posts" :key="post.id" class="table-row">
            <td class="table-cell">
              <div class="flex items-center space-x-3">
                <img
                  v-if="post.cover_image_url"
                  :src="post.cover_image_url"
                  class="w-12 h-12 rounded object-cover flex-shrink-0"
                  alt=""
                />
                <div>
                  <p class="font-medium">{{ post.title }}</p>
                  <p class="text-xs text-content-300 font-mono">/novedades/{{ post.slug }}</p>
                </div>
              </div>
            </td>
            <td class="table-cell">
              <span
                :class="[
                  'p-1 text-xs rounded text-white w-fit inline-block',
                  statusClass(post),
                ]"
                >{{ statusLabel(post) }}</span
              >
            </td>
            <td class="table-cell">{{ formatDate(post.published_at) }}</td>
            <td class="table-cell">{{ post.products_count }}</td>
            <td class="table-cell">
              <div class="flex items-center space-x-2">
                <a
                  v-if="isLive(post)"
                  :href="publicUrl(post)"
                  target="_blank"
                  rel="noopener"
                  class="secondary-button"
                  >Ver</a
                >
                <router-link
                  v-if="hasPermission('edit_posts')"
                  :to="{ name: 'postsEditForm', params: { id: post.id } }"
                  class="secondary-button"
                  >Editar</router-link
                >
                <button
                  v-if="hasPermission('delete_posts')"
                  class="delete-button"
                  @click="confirmDelete = post"
                >
                  Eliminar
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!posts.length">
            <td colspan="5" class="text-center p-4 text-content-300">
              Aún no hay novedades. Crea la primera.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="flex w-full h-40 items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>

    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />

    <div
      v-if="confirmDelete"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
    >
      <div class="card max-w-md w-full mx-4">
        <h3 class="mb-2">Eliminar novedad</h3>
        <p class="text-sm">
          ¿Eliminar <strong>{{ confirmDelete.title }}</strong>? Esta acción no se puede deshacer
          y la página dejará de existir en el sitio.
        </p>
        <div class="flex justify-end space-x-2 mt-4">
          <button class="secondary-button" @click="confirmDelete = null" :disabled="deleting">Cancelar</button>
          <button class="delete-button" @click="deletePost" :disabled="deleting">
            {{ deleting ? "Eliminando…" : "Eliminar" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { websiteUrl } from "@/runtimeConfig";
import Pagination from "@/components/ui/Pagination.vue";

export default {
  name: "Posts",

  components: { Pagination },

  data() {
    return {
      posts: [],
      loading: false,
      currentPage: 1,
      totalPages: 0,
      searchText: "",
      statusFilter: "",
      confirmDelete: null,
      deleting: false,
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "posts");
    this.loadPosts();
  },

  methods: {
    isLive(post) {
      return post.is_published && post.published_at && new Date(post.published_at) <= new Date();
    },

    statusLabel(post) {
      if (!post.is_published) return "Borrador";
      return this.isLive(post) ? "Publicada" : "Programada";
    },

    statusClass(post) {
      if (!post.is_published) return "bg-content-300";
      return this.isLive(post) ? "bg-success-500" : "bg-primary-300";
    },

    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
    },

    publicUrl(post) {
      return `${websiteUrl}/novedades/${post.slug}`;
    },

    async loadPosts() {
      this.loading = true;
      try {
        const response = await api.posts.getAllWithQueryParams([
          { name: "page", value: this.currentPage },
          { name: "search", value: this.searchText },
          { name: "status", value: this.statusFilter },
        ]);
        this.posts = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.last_page || 0;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadPosts();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadPosts();
    },

    async deletePost() {
      if (!this.confirmDelete) return;
      this.deleting = true;
      try {
        const response = await api.posts.delete(this.confirmDelete.id);
        this.$store.dispatch("successSnack", response.message);
        this.confirmDelete = null;
        await this.loadPosts();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
      this.deleting = false;
    },
  },
};
</script>
